// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
} from 'common.data';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  CardBody,
  Table,
} from 'reactstrap';
import {
  CommonDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
  TdImage,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  getImageUrl,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultGetData,
  defaultUpsertItem,
} from '../../../helpers';
import config from '../../../config';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import EntryUnmatchedFields from '../../fields/EntryUnmatchedFields';
import ThQty from '../../common/ThQty';

// Services
import Services from '../../../services';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'RegattaEntry'>>;
  loading: boolean;
};

const pageId = adminPages.adminEntriesUnmatchedPage;

class AdminEntriesUnmatchedPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;

    if (main.activeRegionId !== null && isDiff(
      main.activeRegionId,
      prevProps.main.activeRegionId,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        if (main.activeRegionId === data.RegionId && data.modelName === 'RegattaEntry') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegionId = main.activeRegionId;

    await defaultGetData({
      action: async () => {
        if (RegionId !== null) {
          await this.setStateAsync({
            loading: true,
          });

          const data = await Services.Api.getManyAdminRegattaEntry({
            RegionId,
          });

          await this.setStateAsync({
            data,
            loading: false,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      redirect,
      t,
    } = this.props;

    return (
      <Card id={adminSections[pageId].header}>
        <SectionHeader
          title={t('Unmatched entries')}
          titleButton={(
            <Button
              className={'mr-2'}
              color={'secondary'}
              id={'redirect-admin-entries-page-button'}
              onClick={() => redirect({
                pathname: '/admin/entries',
              })}
              size={'sm'}
            >
              <em className={'fa icon-arrow-left'} />
            </Button>
          )}
        />
      </Card>
    );
  }

  renderButtons(dataItem: $Model<'RegattaEntry'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <CommonDialog
          content={(
            <CardBody>
              {t('Do you really want to add regatta entry?')}
            </CardBody>
            )}
          id={`add-regatta-entry-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelConfirmButton={t('Confirm')}
          onAction={() => defaultUpsertItem({
            action: async () => Services.Api.addAdminRegattaEntry(dataItem),
          })}
          title={t('Need confirmation')}
        >
          <Button
            className={'mr-1'}
            color={'info'}
            id={`add-regatta-entry-button-${id}`}
            size={'sm'}
          >
            {t('Add')}
          </Button>
        </CommonDialog>
        <FormDialog
          fields={(
            <EntryUnmatchedFields item={dataItem} />
          )}
          id={`update-regatta-entry-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'RegattaEntry'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertAdminRegattaEntry(params);
            },
          })}
          subTitle={`${dataItem.RegistrationNo} ${dataItem.Name || ''}`}
          title={t('Regatta entry')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-regatta-entry-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th />
            <th>{t('Reg. no.')}</th>
            <th>{t('Name')}</th>
            <th>{t('Make / Model')}</th>
            <th>{t('Date created')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'RegattaEntry'>) => (
            <tr key={dataItem.id}>
              <TdImage
                largeImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: dataItem.Image,
                  size: 'max',
                })}
                noImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: null,
                  size: 'min',
                })}
                smallImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: dataItem.Image,
                  size: 'min',
                })}
                value={dataItem.Image}
              />
              <td>
                {dataItem.RegistrationNo}
                {dataItem.SailNo !== null && (
                  <small className={'text-muted'}>
                    {dataItem.SailNo}
                  </small>
                )}
              </td>
              <td>{dataItem.Name}</td>
              <td>{dataItem.Model}</td>
              <TdDate
                duration
                multiple
                value={dataItem.createdAt}
              />
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminEntriesUnmatchedPageView;
